"use client"

import React, { useMemo, useState, useEffect } from 'react'
import { Card, CardContent } from '../../../shared/components/ui/card'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts'
import { useRegionId } from '../../../shared/hooks/use-region-id'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import { useFilterSearchHomes, FilterSearchHomesResponse } from '@/features/homeowners/hooks/filter-search-homes.hook'

type OverallStatusData = {
  category: string;
  count: number;
  color: string;
}

type OverallStatusBarChartProps = {
  totalHomesSupported?: number; // Total Homeowners Supported from statusData
}

function hasRelevantRrgWork(statuses: Array<string | null | undefined>): boolean {
  return statuses.some((status) => status && status !== 'No Assistance Required')
}

function isRrgWorkComplete(statuses: Array<string | null | undefined>): boolean {
  const relevantStatuses = statuses.filter((status) => status && status !== 'No Assistance Required')
  return relevantStatuses.length > 0 && relevantStatuses.every((status) => status === 'Complete')
}

function isRecoveryOrGoodwillComplete(
  reliefStatus: string | null | undefined,
  rebuildingStatus: string | null | undefined,
  goodwillStatus: string | null | undefined
): boolean {
  const recoveryStatuses = [reliefStatus, rebuildingStatus]
  if (hasRelevantRrgWork(recoveryStatuses)) {
    return isRrgWorkComplete(recoveryStatuses)
  }

  return goodwillStatus === 'Complete'
}

/** Y-axis max: scales to data so small regions (e.g. single digits) aren't crushed under a 450 ceiling. */
function computeOverallStatusYAxisMax(maxBarValue: number): number {
  if (maxBarValue <= 0) return 10
  const padded = maxBarValue * 1.12 + 1
  if (maxBarValue <= 18) {
    return Math.max(20, Math.ceil(padded / 5) * 5)
  }
  if (padded <= 100) return Math.ceil(padded / 10) * 10
  if (padded <= 500) return Math.ceil(padded / 50) * 50
  if (padded <= 1000) return Math.ceil(padded / 100) * 100
  return Math.ceil(padded / 250) * 250
}

function OverallStatusBarChart({ totalHomesSupported }: OverallStatusBarChartProps) {
  const { regionId, isLoading: isLoadingRegion } = useRegionId()
  const shouldFetchHomes = !isLoadingRegion && regionId !== null && regionId !== undefined
  const { data: allHomesData, isLoading, error: fetchError } = useFilterSearchHomes(
    shouldFetchHomes ? regionId ?? undefined : undefined,
    true, // includeInactive = true to get all homes
    { enabled: shouldFetchHomes }
  )
  const [priorityData, setPriorityData] = useState<any[]>([])

  // Fetch priority data to get unique homes requiring workscope
  useEffect(() => {
    const fetchPriorityData = async () => {
      if (!regionId) {
        setPriorityData([])
        return
      }
      try {
        const res = await authFetchJson<any>(`/priority?regionId=${encodeURIComponent(regionId)}`)
        if (res.success && res.data) {
          const data = res.data
          setPriorityData(Array.isArray(data) ? data : [])
        }
      } catch (error) {
        console.error('Error fetching priority data:', error)
      }
    }
    fetchPriorityData()
  }, [regionId])

  // Calculate unique homes from priority data (same as Priority Matrix: ready + notReady + goodwill)
  // This matches the "Unique Homes" count shown in Homeowners → Priority tab.
  const uniqueHomesRequiringWorkscope = useMemo(() => {
    if (!priorityData || priorityData.length === 0) {
      return 0
    }
    const uniqueHomeIds = new Set<number>()
    priorityData.forEach((ws: any) => {
      // Add home IDs from ready homes
      ws.priorities?.ready?.forEach((home: any) => {
        const homeId = typeof home === 'object' ? home.homeId : home
        if (homeId) uniqueHomeIds.add(homeId)
      })
      // Add home IDs from not ready homes
      ws.priorities?.notReady?.forEach((home: any) => {
        const homeId = typeof home === 'object' ? home.homeId : home
        if (homeId) uniqueHomeIds.add(homeId)
      })
      // Add home IDs from goodwill (so count matches Priority Matrix "Unique Homes")
      ws.priorities?.goodwill?.forEach((home: any) => {
        const homeId = typeof home === 'object' ? home.homeId : home
        if (homeId) uniqueHomeIds.add(homeId)
      })
    })
    return uniqueHomeIds.size
  }, [priorityData])

  const data = useMemo(() => {
    if (!allHomesData) {
      return []
    }

    const response = allHomesData as FilterSearchHomesResponse
    if (!response.success || !response.data) {
      return []
    }

    const homes = response.data

    // Filter to only ACTIVE homes (isReported = true)
    const activeHomes = homes.filter((home) => home.isReported === true)

    // Use totalHomesSupported if provided (to match Total Homeowners Supported), otherwise count active homes
    const totalHomes = totalHomesSupported ?? activeHomes.length

    // Filter active homes that have an overallStatus for category calculations
    const activeHomesWithStatus = activeHomes.filter((home) => {
      const overallStatus = home.homeStatusInfo?.overallStatus
      return overallStatus && overallStatus.trim() !== ''
    })

    // Calculate counts for each category based on active homes with status
    const fullyComplete = activeHomesWithStatus.filter((home) => {
      const status = home.homeStatusInfo?.overallStatus
      return status === 'Complete' || status === 'Fully Complete' || status === '100%'
    }).length

    const fullyAndFunctionallyComplete = activeHomes.filter((home) => {
      return (
        home.homeStatusInfo?.overallStatus === 'Functionally Complete' ||
        isRecoveryOrGoodwillComplete(home.reliefStatus, home.rebuildingStatus, home.goodwillStatus)
      )
    }).length

    const supportOnly = activeHomes.filter((home) => {
      return (
        home.reliefStatus === 'No Assistance Required' &&
        home.rebuildingStatus === 'No Assistance Required' &&
        home.goodwillStatus === 'No Assistance Required'
      )
    }).length

    const demo = activeHomesWithStatus.filter((home) => {
      const status = home.homeStatusInfo?.overallStatus
      return status === 'Demo'
    }).length

    // Total Homes w/ Workscope: count each active home once when any computed R/R/G status
    // indicates non-homeowner relief, rebuild, or goodwill work.
    const totalHomesWorked = activeHomes.filter((home) => {
      const statuses = [home.reliefStatus, home.rebuildingStatus, home.goodwillStatus]
      return hasRelevantRrgWork(statuses)
    }).length

    // Current Homes w/ Workscope = unique homes from Priority Matrix (ready + notReady + goodwill)
    // Matches "Unique Homes" in Homeowners → Priority tab.
    return [
      {
        category: 'Total Homes',
        count: totalHomes,
        color: 'hsl(var(--foreground))'
      },
      {
        category: 'Total Homes w/ Workscope',
        count: totalHomesWorked,
        color: 'hsl(var(--primary))'
      },
      {
        category: 'Complete & Functionally Complete',
        count: fullyAndFunctionallyComplete,
        color: '#3E7C59'
      },
      {
        category: 'Current Homes w/ Workscope',
        count: uniqueHomesRequiringWorkscope,
        color: '#C9A961'
      },
      {
        category: 'Support Only',
        count: supportOnly,
        color: 'hsl(var(--muted-foreground))'
      },
      {
        category: 'Demo',
        count: demo,
        color: 'hsl(var(--destructive))'
      }
    ]
  }, [allHomesData, totalHomesSupported, uniqueHomesRequiringWorkscope])

  const yAxisMax = useMemo(() => {
    if (!data.length) return 450
    const maxBar = Math.max(0, ...data.map((d) => Number(d.count) || 0))
    return computeOverallStatusYAxisMax(maxBar)
  }, [data])

  const isLoadingData = isLoadingRegion || isLoading

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading overall status data...</span>
      </div>
    )
  }

  if (!regionId) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No region selected</p>
      </div>
    )
  }

  if (fetchError || data.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{fetchError?.message || 'No overall status data available'}</p>
      </div>
    )
  }

  return (
    <Card className="shadow-md rounded-2xl">
      <CardContent className="h-[400px] p-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <XAxis 
              dataKey="category" 
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fontSize: 12, fill: '#374151' }}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#374151' }}
              domain={[0, yAxisMax]}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: 'rgba(128,128,128,0.1)' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const item = payload[0].payload as OverallStatusData
                  return (
                    <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                      <p className="font-semibold text-gray-800 mb-1">{item.category}</p>
                      <p className="text-sm text-gray-600">{item.count}</p>
                    </div>
                  )
                }
                return null
              }}
            />
            <Bar 
              dataKey="count" 
              radius={[4, 4, 0, 0]}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
              <LabelList 
                dataKey="count" 
                position="top"
                fill="#374151"
                fontSize={12}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

export default OverallStatusBarChart

